import {
	Eye,
	EyeOff,
	FileQuestion,
	FlagTriangleRight,
	Pencil,
	Plus,
	Save,
	Trash2,
	X,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '@/ui/components/ui/breadcrumb';
import { Button } from '@/ui/components/ui/button';
import { Card, CardContent, CardHeader } from '@/ui/components/ui/card';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/ui/components/ui/dialog';
import { Input } from '@/ui/components/ui/input';
import { Label } from '@/ui/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/ui/components/ui/select';
import { Separator } from '@/ui/components/ui/separator';
import { StatusPlaceholder } from '@/ui/components/ui/status-placeholder';
import { Textarea } from '@/ui/components/ui/textarea';
import { AdminLayout } from '../components/AdminLayout';
import { FRStatusBadge } from '../components/project/feature-requests/FRStatusBadge';
import type { AdminFeatureRequest, NoteObject } from '../hooks/use-admin';
import {
	FR_STATUSES,
	useAdminCreateNote,
	useAdminDeleteNote,
	useAdminFeatureRequests,
	useAdminNotes,
	useAdminProject,
	useAdminToggleHideFR,
	useAdminToggleModerationFlagFR,
	useAdminUpdateFR,
	useAdminUpdateFRStatus,
	useAdminUpdateNote,
} from '../hooks/use-admin';
import { formatEnum } from '../utils/format-enum';

export function AdminFeatureRequestPage() {
	const { id: projectId, frId } = useParams<{ id: string; frId: string }>();
	const location = useLocation();
	const [updateFRStatus] = useAdminUpdateFRStatus();
	const [updateFR] = useAdminUpdateFR();
	const [toggleHide] = useAdminToggleHideFR();
	const [toggleModerationFlag] = useAdminToggleModerationFlagFR();
	const [createNote] = useAdminCreateNote();
	const [updateNote] = useAdminUpdateNote();
	const [deleteNote] = useAdminDeleteNote();

	const stateFR = (location.state as { fr?: AdminFeatureRequest } | null)?.fr;
	const { data: projectData } = useAdminProject(projectId ?? '');
	const { data: frData } = useAdminFeatureRequests(projectId ?? '');
	const { data: notesData, refetch: refetchNotes } = useAdminNotes(
		frId ?? '',
	);

	const project = projectData?.adminProject ?? null;
	const canEdit = project?.canEdit ?? false;
	const fr: AdminFeatureRequest | null =
		frData?.adminFeatureRequests?.find((f) => f.id === frId) ??
		stateFR ??
		null;

	const notes: NoteObject[] = notesData?.adminNotes ?? [];

	const [editMode, setEditMode] = useState(false);
	const [editTitle, setEditTitle] = useState('');
	const [editStatus, setEditStatus] = useState('');
	const [editDescription, setEditDescription] = useState('');
	const [saving, setSaving] = useState(false);
	const [confirmHideOpen, setConfirmHideOpen] = useState(false);
	const [confirmModerationOpen, setConfirmModerationOpen] = useState(false);

	// Note state
	const [newNoteBody, setNewNoteBody] = useState('');
	const [addingNote, setAddingNote] = useState(false);
	const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
	const [editingNoteBody, setEditingNoteBody] = useState('');

	const startEdit = () => {
		if (!fr) return;
		setEditTitle(fr.title);
		setEditStatus(fr.status);
		setEditDescription(fr.description ?? '');
		setEditMode(true);
	};

	const handleSave = async () => {
		if (!fr) return;
		setSaving(true);
		try {
			const titleChanged = editTitle !== fr.title;
			const descChanged = editDescription !== (fr.description ?? '');
			if (titleChanged || descChanged) {
				await updateFR({
					variables: {
						input: {
							featureRequestId: fr.id,
							...(titleChanged ? { title: editTitle } : {}),
							...(descChanged
								? { description: editDescription }
								: {}),
						},
					},
				});
			}
			if (editStatus !== fr.status) {
				await updateFRStatus({
					variables: {
						input: { featureRequestId: fr.id, status: editStatus },
					},
				});
			}
			setEditMode(false);
		} finally {
			setSaving(false);
		}
	};

	const handleToggleHide = async () => {
		if (!fr) return;
		await toggleHide({
			variables: {
				input: { featureRequestId: fr.id, hide: !fr.hide },
			},
		});
	};

	const handleToggleModerationFlag = async () => {
		if (!fr) return;
		await toggleModerationFlag({
			variables: {
				input: {
					featureRequestId: fr.id,
					isFlaggedByModeration: !fr.isFlaggedByModeration,
				},
			},
		});
	};

	const handleAddNote = async () => {
		if (!frId || !newNoteBody.trim()) return;
		setAddingNote(true);
		try {
			await createNote({
				variables: {
					input: { featureRequestId: frId, body: newNoteBody.trim() },
				},
			});
			setNewNoteBody('');
		} finally {
			setAddingNote(false);
		}
	};

	const handleUpdateNote = async (noteId: string) => {
		if (!editingNoteBody.trim()) return;
		await updateNote({
			variables: { id: noteId, body: editingNoteBody.trim() },
		});
		setEditingNoteId(null);
		setEditingNoteBody('');
	};

	const handleDeleteNote = async (noteId: string) => {
		await deleteNote({ variables: { id: noteId } });
		refetchNotes();
	};

	const formatDate = (ts: string) =>
		new Date(Number(ts)).toLocaleDateString('en-GB', {
			day: '2-digit',
			month: 'short',
			year: '2-digit',
		});

	return (
		<AdminLayout>
			<Breadcrumb className='mb-6'>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbLink asChild>
							<Link to='/admin'>Projects</Link>
						</BreadcrumbLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbLink asChild>
							<Link to={`/admin/projects/${projectId}`}>
								{project?.name ?? 'Project'}
							</Link>
						</BreadcrumbLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbPage>
							{fr?.title ?? 'Feature request'}
						</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>

			{fr ? (
				<Card className='py-0'>
					<CardHeader className='bg-muted/40 border-b px-6 py-4'>
						<div className='flex items-start justify-between gap-6'>
							<div className='flex-1 min-w-0 flex flex-col gap-2'>
								{editMode ? (
									<Input
										value={editTitle}
										onChange={(e) =>
											setEditTitle(e.target.value)
										}
										className='text-base font-semibold h-auto py-1.5 max-w-lg'
									/>
								) : (
									<h1 className='text-lg font-semibold text-foreground leading-tight'>
										{fr.title}
									</h1>
								)}
								<div className='flex items-center gap-3 flex-wrap text-xs text-muted-foreground'>
									<span>
										Created{' '}
										<span className='text-foreground'>
											{formatDate(fr.createdAt)}
										</span>
									</span>
									<span className='w-px h-3 bg-border' />
									<span>
										Updated{' '}
										<span className='text-foreground'>
											{formatDate(fr.updatedAt)}
										</span>
									</span>
								</div>
								<div className='flex items-center gap-3 flex-wrap text-xs text-muted-foreground'>
									{editMode ? (
										<Select
											value={editStatus}
											onValueChange={setEditStatus}
										>
											<SelectTrigger className='h-7 w-36 text-xs'>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{FR_STATUSES.map((s) => (
													<SelectItem
														key={s}
														value={s}
														className='text-xs'
													>
														{formatEnum(s)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									) : (
										<FRStatusBadge status={fr.status} />
									)}
								</div>
							</div>

							<div className='flex flex-col items-end gap-3 shrink-0'>
								{canEdit && (
									<div className='flex items-center gap-1'>
										<Button
											size='sm'
											variant='ghost'
											title={fr.hide ? 'Unhide' : 'Hide'}
											onClick={() =>
												setConfirmHideOpen(true)
											}
										>
											{fr.hide ? (
												<Eye className='size-3.5' />
											) : (
												<EyeOff className='size-3.5' />
											)}
										</Button>
										<Button
											size='sm'
											variant='ghost'
											onClick={() =>
												setConfirmModerationOpen(true)
											}
										>
											<FlagTriangleRight
												className={
													fr.isFlaggedByModeration
														? 'size-3.5 text-red-500'
														: 'size-3.5 text-green-500'
												}
											/>
										</Button>
										<Button
											size='sm'
											variant='ghost'
											disabled={editMode}
											onClick={startEdit}
										>
											<Pencil className='size-3.5' />
										</Button>
									</div>
								)}
								<div className='flex flex-col items-center justify-center bg-primary text-primary-foreground rounded-md px-4 py-2 min-w-14'>
									<span className='text-xl font-bold leading-none'>
										{fr.upvoteCount}
									</span>
									<span className='text-xs mt-1 opacity-80'>
										Votes
									</span>
								</div>
							</div>
						</div>
					</CardHeader>

					<CardContent className='p-6 flex flex-col gap-6'>
						{/* Description */}
						<div className='flex flex-col gap-1.5'>
							<Label className='text-xs text-muted-foreground'>
								Description
							</Label>
							{editMode ? (
								<Textarea
									rows={4}
									value={editDescription}
									onChange={(e) =>
										setEditDescription(e.target.value)
									}
									placeholder='Add a description…'
								/>
							) : fr.description ? (
								<p className='text-sm'>{fr.description}</p>
							) : (
								<p className='text-sm text-muted-foreground italic'>
									No description.
								</p>
							)}
						</div>

						{editMode && (
							<div className='flex justify-end gap-2'>
								<Button
									variant='outline'
									size='sm'
									onClick={() => setEditMode(false)}
								>
									<X className='size-3.5 mr-1.5' />
									Cancel
								</Button>
								<Button
									size='sm'
									disabled={saving}
									onClick={handleSave}
								>
									<Save className='size-3.5 mr-1.5' />
									{saving ? 'Saving…' : 'Save'}
								</Button>
							</div>
						)}

						<Separator />

						{/* Notes */}
						<div className='flex flex-col gap-3'>
							<Label className='text-xs text-muted-foreground'>
								Internal Notes
							</Label>

							{notes.length > 0 ? (
								<div className='flex flex-col gap-3'>
									{notes.map((note) => (
										<div
											key={note.id}
											className='rounded-xl bg-muted/50 p-3 flex flex-col gap-2'
										>
											{editingNoteId === note.id ? (
												<>
													<Textarea
														rows={3}
														value={editingNoteBody}
														onChange={(e) =>
															setEditingNoteBody(
																e.target.value,
															)
														}
													/>
													<div className='flex justify-end gap-2'>
														<Button
															variant='outline'
															size='sm'
															onClick={() => {
																setEditingNoteId(
																	null,
																);
																setEditingNoteBody(
																	'',
																);
															}}
														>
															<X className='size-3 mr-1' />
															Cancel
														</Button>
														<Button
															size='sm'
															onClick={() =>
																handleUpdateNote(
																	note.id,
																)
															}
														>
															<Save className='size-3 mr-1' />
															Save
														</Button>
													</div>
												</>
											) : (
												<>
													<p className='text-sm whitespace-pre-wrap'>
														{note.body}
													</p>
													<div className='flex items-center justify-between text-xs text-muted-foreground'>
														<span>
															{formatDate(
																note.createdAt,
															)}
														</span>
														{canEdit && (
															<div className='flex gap-1'>
																<Button
																	variant='ghost'
																	size='sm'
																	className='h-6 px-2'
																	onClick={() => {
																		setEditingNoteId(
																			note.id,
																		);
																		setEditingNoteBody(
																			note.body,
																		);
																	}}
																>
																	<Pencil className='size-3' />
																</Button>
																<Button
																	variant='ghost'
																	size='sm'
																	className='h-6 px-2 text-destructive hover:text-destructive'
																	onClick={() =>
																		handleDeleteNote(
																			note.id,
																		)
																	}
																>
																	<Trash2 className='size-3' />
																</Button>
															</div>
														)}
													</div>
												</>
											)}
										</div>
									))}
								</div>
							) : (
								<p className='text-sm text-muted-foreground italic'>
									No notes yet.
								</p>
							)}

							{/* Add new note */}
							{canEdit && (
								<div className='flex flex-col gap-2'>
									<Textarea
										rows={2}
										value={newNoteBody}
										onChange={(e) =>
											setNewNoteBody(e.target.value)
										}
										placeholder='Add an internal note…'
									/>
									<div className='flex justify-end'>
										<Button
											size='sm'
											variant='outline'
											disabled={
												addingNote ||
												!newNoteBody.trim()
											}
											onClick={handleAddNote}
										>
											<Plus className='size-3.5 mr-1.5' />
											{addingNote
												? 'Adding…'
												: 'Add note'}
										</Button>
									</div>
								</div>
							)}
						</div>
					</CardContent>
				</Card>
			) : (
				<Card>
					<CardContent>
						<StatusPlaceholder
							icon={FileQuestion}
							title='Feature request not found'
							description='This feature request may have been deleted or does not exist.'
							variant='error'
						/>
					</CardContent>
				</Card>
			)}

			<Dialog open={confirmHideOpen} onOpenChange={setConfirmHideOpen}>
				<DialogContent showCloseButton={false}>
					<DialogHeader>
						<DialogTitle>
							{fr?.hide
								? 'Unhide feature request?'
								: 'Hide feature request?'}
						</DialogTitle>
						<DialogDescription>
							{fr?.hide
								? 'This will make the feature request visible to users again.'
								: 'This will hide the feature request from users. You can unhide it at any time.'}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant='outline'
							onClick={() => setConfirmHideOpen(false)}
						>
							Cancel
						</Button>
						<Button
							variant={fr?.hide ? 'default' : 'destructive'}
							onClick={() => {
								handleToggleHide();
								setConfirmHideOpen(false);
							}}
						>
							{fr?.hide ? 'Unhide' : 'Hide'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog
				open={confirmModerationOpen}
				onOpenChange={setConfirmModerationOpen}
			>
				<DialogContent showCloseButton={false}>
					<DialogHeader>
						<DialogTitle>
							{fr?.isFlaggedByModeration
								? 'Clear moderation flag?'
								: 'Flag as toxic?'}
						</DialogTitle>
						<DialogDescription>
							{fr?.isFlaggedByModeration
								? 'This will clear the moderation flag. The feature request will no longer be marked as toxic.'
								: 'This will mark the feature request as toxic. You can clear this flag at any time.'}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant='outline'
							onClick={() => setConfirmModerationOpen(false)}
						>
							Cancel
						</Button>
						<Button
							variant={
								fr?.isFlaggedByModeration
									? 'default'
									: 'destructive'
							}
							onClick={() => {
								handleToggleModerationFlag();
								setConfirmModerationOpen(false);
							}}
						>
							{fr?.isFlaggedByModeration
								? 'Clear flag'
								: 'Flag as toxic'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</AdminLayout>
	);
}
